import { type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import {
  DataTable,
  DataTableHeaderRow,
  DataTableTable,
  DataTableTbody,
  DataTableTd,
  DataTableTh,
  DataTableThead,
  DataTableTr,
} from '@/components/ui/data-table';
import { Skeleton } from '@/components/ui/skeleton';

const SKELETON_ROW_COUNT = 8;
/** Mirrors the column widths in BrowseFamiliesTableBody. */
const CELL_WIDTHS = ['w-32', 'w-20', 'w-10', 'w-8', 'w-16'] as const;

/**
 * Layout-stable placeholder for the BrowseFamilies page. Same shape rules as
 * `BrowsePatientsSkeleton`: same column widths as the real table so the
 * pending → loaded transition does not reflow.
 */
export function BrowseFamiliesSkeleton(): ReactElement {
  const { t } = useTranslation();

  return (
    <div
      role="status"
      aria-busy="true"
      aria-label={t('routes.browseFamilies.loading')}
      className="space-y-4"
    >
      <div className="flex flex-wrap items-end gap-3">
        <Skeleton className="h-9 w-full max-w-sm flex-1 min-w-[180px]" />
      </div>

      <DataTable>
        <div
          className="flex flex-wrap items-center justify-between gap-3 border-b border-border bg-white px-3 py-2.5 text-xs text-muted-foreground dark:bg-card sm:px-4"
          aria-hidden="true"
        >
          <div className="flex items-center gap-2">
            <Skeleton className="h-3 w-10" />
            <Skeleton className="h-8 w-14" />
            <Skeleton className="h-3 w-16" />
          </div>
          <div className="flex items-center gap-1">
            <Skeleton className="h-3 w-24 mr-2" />
            <Skeleton className="h-8 w-8 rounded-md" />
            <Skeleton className="h-8 w-8 rounded-md" />
          </div>
        </div>

        <DataTableTable>
          <DataTableThead>
            <DataTableHeaderRow>
              <DataTableTh widthClassName="w-[28%] min-w-[8rem]">
                <Skeleton className="h-3 w-20" />
              </DataTableTh>
              <DataTableTh widthClassName="w-40">
                <Skeleton className="h-3 w-24" />
              </DataTableTh>
              <DataTableTh widthClassName="w-20">
                <Skeleton className="h-3 w-10" />
              </DataTableTh>
              <DataTableTh className="w-24" align="right">
                <Skeleton className="h-3 w-8 ml-auto" />
              </DataTableTh>
              <DataTableTh className="w-32" widthClassName="w-32" align="right">
                <Skeleton className="h-3 w-16 ml-auto" />
              </DataTableTh>
            </DataTableHeaderRow>
          </DataTableThead>
          <DataTableTbody>
            {Array.from({ length: SKELETON_ROW_COUNT }, (_, rowIdx) => (
              <DataTableTr key={`skeleton-${String(rowIdx)}`}>
                {CELL_WIDTHS.map((width, cellIdx) => {
                  const rightAligned = cellIdx === 3 || cellIdx === 4;
                  return (
                    <DataTableTd key={cellIdx} align={rightAligned ? 'right' : 'left'}>
                      <Skeleton
                        className={`h-3.5 ${width} ${rightAligned ? 'ml-auto' : ''}`}
                      />
                    </DataTableTd>
                  );
                })}
              </DataTableTr>
            ))}
          </DataTableTbody>
        </DataTableTable>
      </DataTable>
    </div>
  );
}
